import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import NavSearch from 'components/Layout/NavSearch';
import { Timestamp } from 'firebase/firestore';
import {
  fetchVouchers,
  deleteVoucher,
  clearVouchersData,
} from '../../state/actions/vouchers';
import { sortDate, sortDateVoucher } from '../../utils';

const Vouchers = () => {
  const { voucherList, isAdmin, error, loading, deleted } = useSelector(
    (state) => ({
      voucherList: state.vouchers.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.vouchers.error,
      loading: state.vouchers.loading,
      deleted: state.vouchers.deleted,
    }),
    shallowEqual,
  );
  const [deleteModal, setDeleteModal] = useState({
    catId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState();

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchVouchers(true));
    }

    return () => dispatch(clearVouchersData());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        catId: null,
        isOpen: false,
      }));
    }
  }, [deleted, loading]);

  const redirect = !isAdmin && <Navigate to={paths.ROOT} />;

  const onRemoveButtonClickHandler = (catId) => {
    setDeleteModal((prevState) => ({
      catId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ catId: null, isOpen: false });
  };

  const onDeleteLevelHandler = () => {
    dispatch(deleteVoucher(deleteModal.catId));
  };

  const columns = [
    {
      Header: 'Voucher Rule Name',
      accessor: 'name',
    },
    {
      Header: 'Voucher Rule Identity',
      accessor: 'id',
    },
    {
      Header: 'Fixed Amount',
      accessor: 'amount_off',
    },
    {
      Header: 'Percentage off',
      accessor: 'percent_off',
    },
    {
      Header: 'Max redemptions',
      accessor: 'max_redemptions',
    },
    {
      Header: 'Expired at',
      accessor: 'redeem_by',
      sortType: sortDateVoucher,
      Cell: ({ row }) =>
        row.original.redeem_by && (
          <small className="has-text-grey is-abbr-like">
            {row.original.createdAt instanceof Timestamp &&
              useFormatDate(new Date(row.original.redeem_by), {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
          </small>
        ),
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      sortType: sortDate,
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.createdAt instanceof Timestamp &&
            useFormatDate(row.original.createdAt.toDate(), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="button-actions is-right">
          <Link
            to={`/voucher/${row.original.id}`}
            className="btn btn-sm btn-primary"
          >
            Detail
          </Link>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => onRemoveButtonClickHandler(row.original.id)}
          >
            <i className="feather icon-trash" />
          </button>
        </div>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? voucherList.filter((el) => {
        const clonedElem = { name: el.name, id: el.id };
        return Object.values(clonedElem).some((field) =>
          field.toString().toLowerCase().includes(search.toLowerCase()),
        );
      })
    : voucherList;

  const deleteMessage = useFormatMessage('Levels.delete');

  const confirmMessage = useFormatMessage('Levels.confirm');

  const permDeleteMessage = useFormatMessage('Levels.permDelete');

  const cancelMessage = useFormatMessage('Levels.cancel');

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteLevelHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <Card>
        <Card.Header>
          <div className="d-flex flex-row align-items-center">
            <Card.Title as="h5">Manage Vouchers</Card.Title>
            <NavSearch handleChange={setSearch} />
            <Link to={paths.ADD_VOUCHER} className="btn btn-primary ms-auto">
              Add new
            </Link>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default Vouchers;
